import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './Withdrawal.css';
import { useAuth } from '../context/AuthContext';
import { isKycApproved } from '../utils/kyc';

function Withdrawal() {
  const { getKYCStatus, isAdmin } = useAuth();
  const [wallet, setWallet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('withdraw');
  const [withdrawalForm, setWithdrawalForm] = useState({
    currency: 'USDT',
    amount: '',
    to_address: '',
    network: 'ERC20'
  });
  const [feeInfo, setFeeInfo] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [kycLoading, setKycLoading] = useState(true);

  const isKYCVerified = useMemo(() => {
    return isAdmin() || isKycApproved(kycStatus?.status) || isKycApproved(getKYCStatus());
  }, [kycStatus, getKYCStatus, isAdmin]);

  const canWithdraw = isKYCVerified;

  const fetchKYCStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setKycLoading(false);
        return;
      }

      const response = await axios.get('https://trcsite.net/api/kyc/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setKycStatus(response.data.kyc_application);
      setKycLoading(false);
    } catch (error) {
      console.error('Error fetching KYC status:', error);
      setKycStatus(null);
      setKycLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWallet();
    fetchWithdrawalHistory();
    fetchKYCStatus();
  }, [fetchKYCStatus]);

  useEffect(() => {
    if (withdrawalForm.currency && withdrawalForm.amount && canWithdraw) {
      calculateFee();
    }
  }, [withdrawalForm.currency, withdrawalForm.amount, canWithdraw]);

  const fetchWallet = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://trcsite.net/api/wallet', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallet(response.data.wallets || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching wallet:', error);
      setLoading(false);
    }
  };

  const fetchWithdrawalHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://trcsite.net/api/wallet/withdrawals/history', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWithdrawalHistory(response.data.withdrawals || []);
    } catch (error) {
      console.error('Error fetching withdrawal history:', error);
    }
  };

  const calculateFee = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://trcsite.net/api/wallet/calculate-fee', 
        {
          currency: withdrawalForm.currency,
          amount: parseFloat(withdrawalForm.amount) || 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeeInfo(response.data);
    } catch (error) {
      console.error('Error calculating fee:', error);
    }
  };

  const submitWithdrawal = async (e) => {
    e.preventDefault();
    
    if (!isKYCVerified) {
      alert('❌ KYC Verification Required\n\nPlease complete KYC verification before making withdrawals.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://trcsite.net/api/wallet/withdraw', 
        withdrawalForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('✅ Withdrawal request submitted successfully! It will be processed within 24 hours.');
      setWithdrawalForm({
        currency: 'USDT',
        amount: '',
        to_address: '',
        network: 'ERC20'
      });
      setFeeInfo(null);
      fetchWithdrawalHistory();
      fetchWallet();
    } catch (error) {
      console.error('Error submitting withdrawal:', error);
      alert('❌ Failed to submit withdrawal: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#00c853';
      case 'pending': return '#ffa726';
      case 'rejected': return '#ff1744';
      default: return '#b0b8c4';
    }
  };

  const getSelectedWalletBalance = () => {
    const selectedWallet = wallet.find(w => w.currency === withdrawalForm.currency);
    return selectedWallet ? parseFloat(selectedWallet.balance) : 0;
  };

  const setMaxAmount = () => {
    if (!canWithdraw) {
      alert('Please complete verification to withdraw funds');
      return;
    }
    
    const balance = getSelectedWalletBalance();
    if (feeInfo && feeInfo.fee) {
      const maxAmount = balance - feeInfo.fee;
      setWithdrawalForm({...withdrawalForm, amount: Math.max(0, maxAmount).toFixed(8)});
    } else {
      setWithdrawalForm({...withdrawalForm, amount: balance.toFixed(8)});
    }
  };

  if (loading || kycLoading) {
    return (
      <div className="withdrawal-loading-container">
        <div className="loading-spinner-large"></div>
        <div className="loading-title">Loading Withdrawal</div>
        <div className="loading-subtitle">Checking verification status...</div>
      </div>
    );
  }

  return (
    <div className="withdrawal-container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '35px' }}>
      {/* Verification Warning Banner */}
      

      <div className="section-header" style={{ marginBottom: '30px' }}>
        <h1>Withdraw Funds</h1>
        <p style={{ color: '#b0b8c4' }}>Withdraw funds from your trading account</p>
        
        {/* Verification Status */}
        <div style={{ 
          display: 'flex', 
          gap: '20px', 
          marginTop: '15px',
          fontSize: '14px',
          flexWrap: 'wrap'
        }}>
          <div className={`verification-status ${isKYCVerified ? 'verified' : 'pending'}`}>
            <span className="status-icon">
              {isKYCVerified ? '✓' : '✗'}
            </span>
            <span className="status-text">
              KYC {isKYCVerified ? 'Verified' : 'Pending'}
            </span>
          </div>
        </div>
      </div>

      {/* Wallet Balances */}
      <div className="wallet-section" style={{ marginBottom: '40px' }}>
        <h2 style={{ color: '#ffffff', marginBottom: '20px' }}>Your Wallet Balances</h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
          gap: '15px',
          marginBottom: '30px'
        }}>
          {wallet.map((walletItem) => (
            <div key={walletItem.currency} style={{
              backgroundColor: '#1a2332',
              border: '1px solid #344258',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              opacity: canWithdraw ? 1 : 0.6
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffffff', marginBottom: '10px' }}>
                {walletItem.currency}
              </div>
              <div style={{ fontSize: '24px', color: '#2962ff', marginBottom: '10px' }}>
                {parseFloat(walletItem.balance).toFixed(8)}
              </div>
              <div style={{ fontSize: '12px', color: '#b0b8c4' }}>
                Available
              </div>
              {!canWithdraw && (
                <div style={{ 
                  fontSize: '10px', 
                  color: '#ffa726', 
                  marginTop: '5px',
                  fontStyle: 'italic'
                }}>
                  Verification required
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '10px', position: 'relative' }}>
        {/* Verification Block Overlay */}
        {!canWithdraw && (
          <div className="verification-block-overlay" style={{
            position: 'fixed',
            top: 0,}}>
            
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #344258',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => setActiveTab('withdraw')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'withdraw' ? '#2962ff' : 'transparent',
              color: '#ffffff',
              border: 'none',
              borderBottom: activeTab === 'withdraw' ? '2px solid #2962ff' : 'none',
              cursor: canWithdraw ? 'pointer' : 'not-allowed',
              opacity: canWithdraw ? 1 : 0.6
            }}
            disabled={!canWithdraw}
          >
            Withdraw Funds
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'history' ? '#2962ff' : 'transparent',
              color: '#ffffff',
              border: 'none',
              borderBottom: activeTab === 'history' ? '2px solid #2962ff' : 'none',
              cursor: 'pointer'
            }}
          >
            Withdrawal History
          </button>
        </div>

        {/* Withdrawal Form */}
        {activeTab === 'withdraw' && (
          <div style={{
            backgroundColor: '#1a2332',
            border: '1px solid #344258',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '500px',
            position: 'relative',
            opacity: canWithdraw ? 1 : 0.6
          }}>
            <h3 style={{ color: '#ffffff', marginBottom: '20px' }}>Withdraw Funds</h3>
            <form onSubmit={submitWithdrawal}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '5px' }}>
                  Currency
                </label>
                <select
                  value={withdrawalForm.currency}
                  onChange={(e) => setWithdrawalForm({...withdrawalForm, currency: e.target.value, amount: ''})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a3b52',
                    border: '1px solid #344258',
                    borderRadius: '4px',
                    color: '#ffffff'
                  }}
                  disabled={!canWithdraw}
                >
                  <option value="USDT">USDT</option>
                  <option value="BTC">BTC</option>
                  <option value="ETH">ETH</option>
                  <option value="BNB">BNB</option>
                </select>
                <div style={{ fontSize: '12px', color: '#b0b8c4', marginTop: '5px' }}>
                  Balance: {getSelectedWalletBalance().toFixed(8)} {withdrawalForm.currency}
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '5px' }}>
                  Amount
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="number"
                    step="0.00000001"
                    value={withdrawalForm.amount}
                    onChange={(e) => setWithdrawalForm({...withdrawalForm, amount: e.target.value})}
                    placeholder="Enter amount"
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#2a3b52',
                      border: '1px solid #344258',
                      borderRadius: '4px',
                      color: '#ffffff'
                    }}
                    required
                    disabled={!canWithdraw}
                  />
                  <button
                    type="button"
                    onClick={setMaxAmount}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: '#344258',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: canWithdraw ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap',
                      opacity: canWithdraw ? 1 : 0.6
                    }}
                    disabled={!canWithdraw}
                  >
                    MAX
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '5px' }}>
                  Destination Address
                </label>
                <input
                  type="text"
                  value={withdrawalForm.to_address}
                  onChange={(e) => setWithdrawalForm({...withdrawalForm, to_address: e.target.value})}
                  placeholder={`Enter ${withdrawalForm.currency} address`}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a3b52',
                    border: '1px solid #344258',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontFamily: 'monospace'
                  }}
                  required
                  disabled={!canWithdraw}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#b0b8c4', display: 'block', marginBottom: '5px' }}>
                  Network
                </label>
                <select
                  value={withdrawalForm.network}
                  onChange={(e) => setWithdrawalForm({...withdrawalForm, network: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a3b52',
                    border: '1px solid #344258',
                    borderRadius: '4px',
                    color: '#ffffff'
                  }}
                  disabled={!canWithdraw}
                >
                  <option value="ERC20">ERC20 (Ethereum)</option>
                  <option value="TRC20">TRC20 (Tron)</option>
                  <option value="BEP20">BEP20 (Binance Smart Chain)</option>
                  <option value="BTC">Bitcoin Network</option>
                  <option value="BEP2">BEP2 (Binance Chain)</option>
                </select>
              </div>

              {/* Fee Information */}
              {feeInfo && withdrawalForm.amount && canWithdraw && (
                <div style={{
                  backgroundColor: '#2a3b52',
                  border: '1px solid #344258',
                  borderRadius: '6px',
                  padding: '15px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ color: '#ffa726', marginBottom: '10px' }}>Withdrawal Summary</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ color: '#b0b8c4' }}>Amount:</span>
                    <span style={{ color: '#ffffff' }}>{parseFloat(withdrawalForm.amount).toFixed(8)} {withdrawalForm.currency}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ color: '#b0b8c4' }}>Network Fee:</span>
                    <span style={{ color: '#ff6b6b' }}>{feeInfo.fee} {withdrawalForm.currency}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ color: '#b0b8c4' }}>You Will Receive:</span>
                    <span style={{ color: '#00c853' }}>{feeInfo.receive_amount} {withdrawalForm.currency}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ color: '#b0b8c4' }}>Deducted:</span>
                    <span style={{ color: '#ffffff', fontWeight: 'bold' }}>{(feeInfo.total_amount).toFixed(4)} {withdrawalForm.currency}</span>
                  </div>
                </div>
              )}

              {/* Important Information */}
              <div style={{
                backgroundColor: '#2a3b52',
                border: '1px solid #344258',
                borderRadius: '6px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: '#ffa726', marginBottom: '10px' }}>Important:</h4>
                <ul style={{ color: '#b0b8c4', fontSize: '12px', paddingLeft: '20px', margin: 0 }}>
                  <li>Double-check the destination address</li>
                  <li>Withdrawals are processed within 24 hours</li>
                  <li>Minimum withdrawal: 20 USDT (or equivalent)</li>
                  <li>Network fees are deducted from the withdrawal amount</li>
                  <li>Ensure the address supports the selected network</li>
                  {!canWithdraw && (
                    <li style={{ color: '#ffa726', fontWeight: 'bold' }}>
                      Complete KYC verification to enable withdrawals
                    </li>
                  )}
                </ul>
              </div>

              <button
                type="submit"
                disabled={!canWithdraw || !withdrawalForm.amount || !withdrawalForm.to_address || parseFloat(withdrawalForm.amount) <= 0}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: !canWithdraw || !withdrawalForm.amount || !withdrawalForm.to_address || parseFloat(withdrawalForm.amount) <= 0 ? '#6c757d' : '#ff6b6b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: !canWithdraw || !withdrawalForm.amount || !withdrawalForm.to_address || parseFloat(withdrawalForm.amount) <= 0 ? 'not-allowed' : 'pointer',
                  fontSize: '16px'
                }}
              >
                {!canWithdraw ? 'Verification Required' : 'Submit Withdrawal'}
              </button>
            </form>
          </div>
        )}

        {/* Withdrawal History */}
        {activeTab === 'history' && (
          <div>
            <h3 style={{ color: '#ffffff', marginBottom: '20px' }}>Withdrawal History</h3>
            {withdrawalHistory.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#1a2332', borderRadius: '8px', overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#2a3b52' }}>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4' }}>Currency</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4' }}>Amount</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4' }}>Fee</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4' }}>To Address</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4' }}>Status</th>
                      <th style={{ padding: '15px', textAlign: 'left', color: '#b0b8c4' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawalHistory.map((withdrawal) => (
                      <tr key={withdrawal.id} style={{ borderBottom: '1px solid #344258' }}>
                        <td style={{ padding: '15px', fontWeight: 'bold' }}>{withdrawal.currency}</td>
                        <td style={{ padding: '15px' }}>{parseFloat(withdrawal.amount).toFixed(8)}</td>
                        <td style={{ padding: '15px', color: '#ff6b6b' }}>{parseFloat(withdrawal.fee || 0).toFixed(8)}</td>
                        <td style={{ padding: '15px', fontFamily: 'monospace', fontSize: '12px' }}>
                          {withdrawal.to_address ? 
                            `${withdrawal.to_address.slice(0, 10)}...${withdrawal.to_address.slice(-8)}` : 
                            'N/A'
                          }
                        </td>
                        <td style={{ padding: '15px' }}>
                          <span style={{
                            color: getStatusColor(withdrawal.status),
                            fontWeight: 'bold'
                          }}>
                            {withdrawal.status.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '15px' }}>
                          {new Date(withdrawal.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ 
                color: '#b0b8c4', 
                textAlign: 'center', 
                padding: '40px',
                backgroundColor: '#1a2332',
                border: '1px solid #344258',
                borderRadius: '8px'
              }}>
                No withdrawal history found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Withdrawal;